import { useCallback, useEffect, useRef } from 'react'
import { executionsApi } from '../../services/api'
import type { StreamingChatMessage } from '../../hooks/useStreamingMissionChat'
import { storedRunEnded } from './storedRun'

/** Execution statuses of a run that has ended; the chat streams none of them. */
export const ENDED_STATUSES: readonly string[] = ['completed', 'failed', 'cancelled']

/** How long the chat waits before reading a stored record that does not hold the run end yet. */
export const STORED_RUN_RETRY_MS = 3_000
/** Further reads of a stored record that does not hold the run end yet. */
export const STORED_RUN_RETRIES = 2

export interface LaunchedRunEndingOptions {
  /** The run this chat launched and streams; null for a run opened from the address or none. */
  runId: string | null
  /** The live stream reported the run's end. */
  streamEnded: boolean
  /** The chat already knows the run ended from its recorded status. */
  statusEnded: boolean
  /** The live stream is connected. */
  isConnected: boolean
  /** Reads the run's stored host history, from the host the run is recorded on when known. */
  readStoredRun: (runId: string, runHostId: string | undefined) => Promise<StreamingChatMessage[] | null>
  /** The recorded status says the run ended. */
  onStatusEnded: (runId: string, status: string) => void
  /** The run's stored record, to show when it holds what the screen lacks. */
  onStoredRun: (runId: string, stored: StreamingChatMessage[]) => void
}

/**
 * A chat that launched a run shows the run's stored record once the run ends
 * (agion/Agion#2871), whether or not its live stream delivered the run.
 *
 * The run's end comes from the stream, or from the run's recorded status,
 * read when the stream reconnects and when the page becomes visible again: a
 * stream that stopped delivering events may never report the end. Nothing is
 * read on a timer while the stream works. The stored record is read once per
 * run; a record that does not hold the run end yet is read again a bounded
 * number of times, because the host stores the last rows after the end is
 * reported.
 */
export function useLaunchedRunEnding({
  runId, streamEnded, statusEnded, isConnected, readStoredRun, onStatusEnded, onStoredRun,
}: LaunchedRunEndingOptions): void {
  const runIdRef = useRef(runId)
  runIdRef.current = runId
  const readFor = useRef<string | null>(null)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callbacks = useRef({ readStoredRun, onStatusEnded, onStoredRun })
  callbacks.current = { readStoredRun, onStatusEnded, onStoredRun }

  const readRecord = useCallback((id: string, runHostId: string | undefined) => {
    if (readFor.current === id) return
    readFor.current = id
    const attempt = async (left: number) => {
      retryTimer.current = null
      let stored: StreamingChatMessage[] | null = null
      try {
        stored = await callbacks.current.readStoredRun(id, runHostId)
      } catch (err) {
        console.warn('[StreamingCustomerChat] Failed to read the ended run from its host:', err)
      }
      if (runIdRef.current !== id) return
      if (stored) callbacks.current.onStoredRun(id, stored)
      if (left > 0 && (!stored || !storedRunEnded(stored))) {
        retryTimer.current = setTimeout(() => { void attempt(left - 1) }, STORED_RUN_RETRY_MS)
      }
    }
    void attempt(STORED_RUN_RETRIES)
  }, [])

  // Another run, or none: a pending read again is for the run that was shown.
  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current)
    retryTimer.current = null
  }, [runId])

  // The stream reported the end.
  useEffect(() => {
    if (runId && streamEnded) readRecord(runId, undefined)
  }, [runId, streamEnded, readRecord])

  const ended = streamEnded || statusEnded
  const checkStatus = useCallback(async () => {
    const id = runIdRef.current
    if (!id || ended) return
    try {
      const execution = await executionsApi.getExecution(id)
      if (runIdRef.current !== id || !ENDED_STATUSES.includes(execution.status)) return
      callbacks.current.onStatusEnded(id, execution.status)
      readRecord(id, execution.edge_host_id || undefined)
    } catch (err) {
      console.warn('[StreamingCustomerChat] Failed to read the run status:', err)
    }
  }, [ended, readRecord])

  // The stream reconnected after it was lost: the run may have ended meanwhile.
  const wasLost = useRef(false)
  const wasConnected = useRef(false)
  useEffect(() => {
    if (!isConnected) {
      if (wasConnected.current) wasLost.current = true
      return
    }
    wasConnected.current = true
    if (!wasLost.current) return
    wasLost.current = false
    void checkStatus()
  }, [isConnected, checkStatus])

  // The page became visible again: a stream that went quiet meanwhile may have missed the end.
  useEffect(() => {
    if (!runId || ended || typeof document === 'undefined') return
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkStatus()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [runId, ended, checkStatus])

  // A new run starts with its connection history unknown.
  useEffect(() => {
    wasLost.current = false
    wasConnected.current = false
  }, [runId])
}
